import { useEffect, useState, useRef } from "react";
import emailjs from "@emailjs/browser";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { MapPin, Phone, Mail } from "lucide-react";
import Loader from "../loader";
import contactJson from "../../data/contact.json";
import Helmet from "react-helmet";
import LazyOnScroll from "../../ui/LazyOnScroll";

interface OfficeHour {
  day: string;
  hours: string;
}

interface ContactData {
  title: string;
  subtitle: string;
  formTitle: string;
  contactTitle: string;
  contactDescription: string;
  officeAddress: string;
  phone: string;
  email: string;
  officeHours: OfficeHour[];
}

export default function ContactPage() {
  const [contactData, setContactData] = useState<ContactData | null>(null);
  const [loading, setLoading] = useState(true);
  const [showLoader, setShowLoader] = useState(false);

  const formRef = useRef<HTMLFormElement>(null);
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => setShowLoader(true), 300);
    try {
      setContactData(contactJson);
    } catch (error) {
      console.error("Error loading contact page data:", error);
      setContactData(null);
    } finally {
      setLoading(false);
      setShowLoader(false);
    }
    return () => clearTimeout(timer);
  }, []);

  const sendEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formRef.current) return;

    setSending(true);
    setSuccess(null);
    setError(null);

    const formData = new FormData(formRef.current);

    const firstName = formData.get("firstName")?.toString() || "";
    const lastName = formData.get("lastName")?.toString() || "";
    const fullName = `${firstName} ${lastName}`.trim();

    const email = formData.get("email")?.toString() || "";
    const phone = formData.get("phone")?.toString() || "";
    const message = formData.get("message")?.toString() || "";

    const templateParams = {
      name: fullName,
      form_email: email,
      phone_number: phone,
      message: message,
    };

    try {
      await emailjs.send(
        process.env.REACT_APP_SERVICE_ID as string,
        process.env.REACT_APP_TEMPLATE_ID as string,
        templateParams,
        process.env.REACT_APP_PUBLIC_KEY as string
      );
      setSuccess("Message sent successfully!");
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    } catch (err) {
      setError("Failed to send message. Please try again.");
      console.error(err);
    } finally {
      setSending(false);
    }
  };

  if (loading && showLoader) return <Loader />;
  if (loading && !showLoader) return <div style={{ minHeight: "100vh" }}></div>; // avoid flicker before loader shows

  if (!contactData) {
    return (
      <div className="min-h-screen flex justify-center items-center text-lg font-semibold text-red-600">
        Failed to load contact information.
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{contactData.title}</title>
        <meta name="description" content={contactData.subtitle} />
        <meta property="og:image" content="/logo.jpg" />
      </Helmet>
      <div className="flex min-h-screen flex-col">
        <main className="flex-1">
          {/* Hero Section */}
          <LazyOnScroll>
            <section className="py-16 md:py-24 bg-gradient-to-b from-white to-[#f9faf1]">
              <div className="container max-w-screen-2xl mx-auto px-4 md:px-6 text-center flex flex-col items-center">
                <h1 className="text-3xl font-bold tracking-tighter sm:text-4xl md:text-5xl text-[#484331]">
                  {contactData.title || "Get in Touch"}
                </h1>
                <p className="mt-4 max-w-[700px] text-[#484331]/80 md:text-xl">
                  {contactData.subtitle}
                </p>
              </div>
            </section>
          </LazyOnScroll>

          {/* Contact Section */}
          <LazyOnScroll delay={80}>
            <section className="py-16 md:py-24">
              <div className="container max-w-screen-2xl mx-auto px-4 md:px-6 lg:px-12 xl:px-20">
                <div className="grid gap-10 lg:grid-cols-2 lg:gap-16">
                  {/* Contact Form */}
                  <div className="rounded-xl border bg-card p-6 shadow-sm md:p-8">
                    <h2 className="text-2xl font-bold mb-6">
                      {contactData.formTitle || "Send Us a Message"}
                    </h2>
                    <form
                      ref={formRef}
                      onSubmit={sendEmail}
                      className="space-y-6"
                    >
                      <div className="grid gap-4 sm:grid-cols-2">
                        <div className="space-y-2">
                          <label
                            htmlFor="firstName"
                            className="text-sm font-medium"
                          >
                            First name
                          </label>
                          <Input
                            id="firstName"
                            name="firstName"
                            placeholder="Enter your first name"
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <label
                            htmlFor="lastName"
                            className="text-sm font-medium"
                          >
                            Last name
                          </label>
                          <Input
                            id="lastName"
                            name="lastName"
                            placeholder="Enter your last name"
                            required
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <label htmlFor="email" className="text-sm font-medium">
                          Email
                        </label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          placeholder="Enter your email"
                          required
                        />
                      </div>

                      <div className="space-y-2">
                        <label htmlFor="phone" className="text-sm font-medium">
                          Phone
                        </label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          placeholder="Enter your phone number"
                        />
                      </div>

                      <div className="space-y-2">
                        <label
                          htmlFor="message"
                          className="text-sm font-medium"
                        >
                          Message
                        </label>
                        <Textarea
                          id="message"
                          name="message"
                          placeholder="Tell us about your project"
                          className="min-h-[120px]"
                          required
                        />
                      </div>

                      <Button
                        type="submit"
                        disabled={sending}
                        className="w-full h-12 bg-[#484331] hover:bg-[#3e3a2b] text-white font-semibold text-base shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-[1.02] disabled:hover:scale-100"
                      >
                        {sending
                          ? "Sending Your Message..."
                          : "Send My Message"}
                      </Button>
                    </form>

                    {/* Success / Error messages */}
                    {success && (
                      <p className="mt-4 text-green-600 font-medium">
                        {success}
                      </p>
                    )}
                    {error && (
                      <p className="mt-4 text-red-600 font-medium">{error}</p>
                    )}
                  </div>

                  {/* Contact Info */}
                  <div className="space-y-8">
                    <div>
                      <h2 className="text-2xl font-bold mb-6">
                        {contactData.contactTitle || "Contact Information"}
                      </h2>
                      <p className="text-muted-foreground">
                        {contactData.contactDescription}
                      </p>
                    </div>

                    <div className="space-y-6">
                      <div className="flex items-start">
                        <div className="mr-4 h-10 w-10 flex items-center justify-center rounded-full bg-primary/10">
                          <MapPin className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                          <h3 className="font-bold">Our Office</h3>
                          <address className="not-italic text-muted-foreground whitespace-pre-line">
                            {contactData.officeAddress}
                          </address>
                        </div>
                      </div>

                      <div className="flex items-start">
                        <div className="mr-4 h-10 w-10 flex items-center justify-center rounded-full bg-primary/10">
                          <Phone className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                          <h3 className="font-bold">Phone</h3>
                          <p className="text-muted-foreground space-y-1">
                            {contactData.phone
                              .split(",")
                              .map((number, index) => {
                                const trimmed = number.trim();
                                return (
                                  <div key={index}>
                                    <a
                                      href={`tel:${trimmed}`}
                                      className="hover:underline"
                                    >
                                      {trimmed}
                                    </a>
                                  </div>
                                );
                              })}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start">
                        <div className="mr-4 h-10 w-10 flex items-center justify-center rounded-full bg-primary/10">
                          <Mail className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                          <h3 className="font-bold">Email</h3>
                          <p className="text-muted-foreground">
                            <a
                              href={`mailto:${contactData.email}`}
                              className="hover:underline"
                            >
                              {contactData.email}
                            </a>
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Office Hours */}
                    {contactData.officeHours?.length > 0 && (
                      <div className="rounded-xl border bg-card p-6 shadow-sm bg-[#f9faf1]">
                        <h3 className="font-bold mb-4">Office Hours</h3>
                        <ul className="space-y-2">
                          {contactData.officeHours.map((oh, idx) => (
                            <li key={idx} className="flex justify-between">
                              <span>{oh.day}</span>
                              <span>{oh.hours}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </section>
          </LazyOnScroll>
        </main>
      </div>
    </>
  );
}
